import { useRef, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';

/**
 * Aplica debounce a una función, retrasando su ejecución hasta que pase el tiempo de espera tras el último intento de llamada.
 *
 * @param {Function} func - Función a ejecutar con retraso.
 * @param {number} [delay=500] - Tiempo de espera en milisegundos antes de ejecutar la función después del último intento de llamada.
 * @returns {Function} Una función debounced que ejecuta la función original después del tiempo de espera especificado.
 */
export const useDebounce = (func, delay = 500) => {
  const funcRef = useRef(func);

  // Se actualiza la referencia funcRef para mantener la versión actualizada de 'func'
  useEffect(() => {
    funcRef.current = func;
  }, [func]);

  // Se crea y memoriza la versión debounced de 'func', que espera el tiempo especificado antes de ejecutarse
  const debouncedFunc = useMemo(() => {
    // Se utiliza una función anónima para garantizar que 'debouncedFunc' use siempre la referencia actualizada de 'func'
    return debounce((...args) => funcRef.current(...args), delay);
  }, [delay]);

  return debouncedFunc;
};